import React, { useEffect, useState } from "react";
import { api, extractError } from "../lib/api";
import { isAdmin, useAuth } from "../context/AuthContext";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

const NONE = "__none__";

function batchLabel(b) {
  const crop = b.crop_name || b.crop_code || "";
  const type = b.type_name || b.type_code || "";
  const status = b.status || "";
  const meta = [crop, type, status].filter(Boolean).join(" · ");
  return meta ? `${b.code} · ${meta}` : b.code;
}

/**
 * Admin-only: reassign which production batch a purchase belongs to (unloading linkage).
 */
export default function AdminPurchaseProductionBatch({ purchase, onSaved }) {
  const { user } = useAuth();
  const [batches, setBatches] = useState([]);
  const [value, setValue] = useState(
    purchase?.production_batch_id != null ? String(purchase.production_batch_id) : NONE,
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setValue(purchase?.production_batch_id != null ? String(purchase.production_batch_id) : NONE);
  }, [purchase?.id, purchase?.production_batch_id]);

  useEffect(() => {
    if (!isAdmin(user)) return;
    let cancelled = false;
    api
      .get("/production/batches")
      .then((r) => {
        if (!cancelled) setBatches(r.data?.results || []);
      })
      .catch(() => {
        if (!cancelled) setBatches([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!isAdmin(user) || !purchase?.id) return null;

  const currentCode = purchase.production_batch_code || "";
  const dirty =
    (purchase.production_batch_id != null ? String(purchase.production_batch_id) : NONE) !== value;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/unloading/purchase/${purchase.id}/production-batch`, {
        production_batch_id: value === NONE ? null : Number(value),
      });
      toast.success(
        data?.result?.production_batch_code
          ? `Batch set to ${data.result.production_batch_code}`
          : "Production batch cleared",
      );
      onSaved?.(data?.purchase || data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-dashed border-amber-500/50 bg-amber-50/40 p-3 space-y-2"
      data-testid={`admin-purchase-batch-${purchase.id}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-[10px] uppercase tracking-widest text-amber-800/80 font-medium">
          Admin · production batch
        </p>
        {currentCode ? (
          <p className="text-xs font-mono text-muted-foreground">Current: {currentCode}</p>
        ) : (
          <p className="text-xs text-muted-foreground">Not linked to a batch</p>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground">
        Moves the whole truck. To split a recorded unload across mill batches, use Split line on the recorded unloading below.
      </p>
      <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
        <div className="flex-1 min-w-0">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Batch</Label>
          <Select value={value} onValueChange={setValue}>
            <SelectTrigger className="mt-1 h-9 bg-card">
              <SelectValue placeholder="Select batch" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>— None —</SelectItem>
              {batches.map((b) => (
                <SelectItem key={b.id} value={String(b.id)}>
                  {batchLabel(b)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          size="sm"
          disabled={!dirty || busy}
          onClick={save}
          className="bg-bansal-sage hover:bg-bansal-sageDark text-white min-h-9"
        >
          {busy ? "Saving…" : "Update batch"}
        </Button>
      </div>
    </div>
  );
}
